/**
 * Data sources (DEC-42, DEC-71). National IT previews what a load would change, then confirms it in writing.
 * Nothing is saved until the scheduled job picks up the request. The Board does not get this tab.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, useApi, useApiClient } from '../api';
import { formatDate, Load } from '../components';

/** Same phrase as LOAD_CONFIRMATION in packages/shared/src/sources.ts and migration 0035. */
const LOAD_CONFIRMATION = 'Load these changes';

const CHANGE: Record<string, string> = { new: 'New file', updated: 'Updated', removed: 'No longer in the folder' };
const ATTEMPT: Record<string, string> = {
  failed: 'The last request did not finish. Preview again, then check this page.',
  refused: 'That load was not saved. A load from this page runs in production. Preview again there.',
  written: 'The load was saved, but this page could not record which files were loaded. Preview again before another load.',
  blocked: 'That load was not saved. The preview was not ready, or the check found something to fix. Preview again.',
  stale: 'The files changed after that preview. Preview again before loading.',
};

interface FileRow {
  label: string;
  change: string;
  firmCode: string | null;
  kind: string;
}

interface QuestionRow {
  kind: string;
  question: string;
  description?: string;
  family?: string;
  findings: number;
}

interface FirmDetail {
  firmCode: string;
  detailStale: boolean;
  detail: {
    unreviewed: { description: string; riskCodes: string[] }[];
    familyLinked: { riskCode: string; title: string }[];
    notLinked: { riskCode: string; title: string }[];
  } | null;
}

export interface SourcesPage {
  measure: string;
  pending: { requestId: number; kind: string; requestedBy: string; requestedAt: string } | null;
  lastLoad: { loadedBy: string; loadedAt: string } | null;
  lastAttempt?: { kind: string; outcome: string; finishedAt: string } | null;
  preview: {
    previewId: number;
    origin: string;
    createdBy: string;
    createdAt: string;
    ready: boolean;
    filesChanged: number;
    filesNew: number;
    filesUpdated: number;
    filesRemoved: number;
    firmsChanged: number;
    issues: number;
    issueText?: string[];
    listUnreadable?: boolean;
    files: FileRow[];
    questions: QuestionRow[];
    unreviewed: number;
    firms: FirmDetail[];
  } | null;
}

/** A line on the National summary when a preview has something ready to load. A failed read stays quiet. */
export function SourcesReady() {
  const state = useApi<SourcesPage>('/national/sources?banner=1');
  if (state.data?.pending || !state.data?.preview?.ready) return null;
  const n = state.data.preview.filesChanged;
  return (
    <p className="notice">
      New data is ready to load ({n} {n === 1 ? 'file' : 'files'} would change). <Link to="/national?view=sources">Open Data sources</Link>
    </p>
  );
}

export function DataSourcesTab() {
  const state = useApi<SourcesPage>('/national/sources');
  return (
    <Load state={state} what="data sources">
      {(page) => <SourcesBody page={page} reload={state.reload} loading={state.loading} />}
    </Load>
  );
}

function SourcesBody({ page, reload, loading }: { page: SourcesPage; reload: () => void; loading: boolean }) {
  const call = useApiClient();
  const preview = page.preview;
  const previewId = preview?.previewId ?? 0;
  const [confirmation, setConfirmation] = useState('');
  const [confirmationFor, setConfirmationFor] = useState(previewId);
  const [busy, setBusy] = useState<'preview' | 'load' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const waiting = Boolean(page.pending);
  const canLoad = Boolean(preview?.ready && preview.issues === 0 && !waiting);
  if (previewId !== confirmationFor) {
    setConfirmationFor(previewId);
    setConfirmation('');
  }

  async function ask(kind: 'preview' | 'load') {
    setBusy(kind);
    setError(null);
    try {
      if (kind === 'preview') await call('/national/sources/preview', { method: 'POST', body: {} });
      else await call('/national/sources/load', { method: 'POST', body: { confirmation, previewId: preview?.previewId } });
      setConfirmation('');
      reload();
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      setError(e instanceof ApiError ? e.message : 'The request may have gone through. Check again.');
      if (status === 409 || status === 0 || status >= 500) reload();
    } finally {
      setBusy(null);
    }
  }

  return (
    <section aria-labelledby="sources-h" className="national-band">
      <h3 id="sources-h" className="national-band-title">Data sources</h3>
      <p>{page.measure} A preview of the folder runs once a day. When you ask for a preview or a load, the next scheduled check picks it up, about every 15 minutes. Nothing is saved until you confirm a load.</p>
      {page.lastLoad ? (
        <p>Last load from this page: {formatDate(page.lastLoad.loadedAt)}, by {page.lastLoad.loadedBy}.</p>
      ) : (
        <p>No load from this page yet. The portal still shows the data from the earlier load.</p>
      )}
      {!preview && <p>No preview yet. Preview shows what a load would change. Nothing is saved.</p>}
      {page.pending && (
        <p role="status">
          A {page.pending.kind === 'load' ? 'load' : 'preview'} requested by {page.pending.requestedBy} is waiting for the next scheduled check.
        </p>
      )}
      {!page.pending && page.lastAttempt && ATTEMPT[page.lastAttempt.outcome] && (
        <p role="alert">{ATTEMPT[page.lastAttempt.outcome]}</p>
      )}
      {error && (
        <div role="alert" className="notice">
          <p>{error}</p>
        </div>
      )}
      {preview && <PreviewSummary preview={preview} />}
      <div className="actions">
        <button className={canLoad ? 'pill secondary-dark' : 'pill'} type="button" disabled={waiting || busy !== null || loading} onClick={() => ask('preview')}>
          {busy === 'preview' ? 'Requesting…' : 'Preview'}
        </button>
        <button className="pill secondary-dark" type="button" disabled={loading} onClick={() => { setError(null); reload(); }}>Check again</button>
      </div>
      {canLoad && (
        <form
          className="bti-position-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (confirmation === LOAD_CONFIRMATION) void ask('load');
          }}
        >
          <label htmlFor="load-confirm">Type {LOAD_CONFIRMATION} to confirm</label>
          <input id="load-confirm" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} autoComplete="off" />
          <button className="pill" type="submit" disabled={confirmation !== LOAD_CONFIRMATION || busy !== null}>
            {busy === 'load' ? 'Requesting…' : LOAD_CONFIRMATION}
          </button>
        </form>
      )}
      {preview && <PreviewDetail preview={preview} />}
    </section>
  );
}

function PreviewSummary({ preview }: { preview: NonNullable<SourcesPage['preview']> }) {
  return (
    <>
      <p>
        {preview.origin === 'nightly'
          ? `Daily preview from ${formatDate(preview.createdAt)}. `
          : `Preview from ${formatDate(preview.createdAt)}, by ${preview.createdBy}. `}
        {preview.filesChanged === 0
          ? 'Nothing would change.'
          : `${preview.filesChanged} ${preview.filesChanged === 1 ? 'file' : 'files'} would change (${preview.filesNew} new, ${preview.filesUpdated} updated, ${preview.filesRemoved} no longer in the folder).`}
        {preview.unreviewed > 0 ? ` ${preview.unreviewed} ${preview.unreviewed === 1 ? 'description needs' : 'descriptions need'} review.` : ''}
        {preview.issues > 0 ? ` The check found ${preview.issues} ${preview.issues === 1 ? 'file it could not read' : 'files it could not read'}, so a load is not available until they are fixed.` : ''}
      </p>
      {(preview.issueText?.length ?? 0) > 0 && (
        <ul>
          {preview.issueText!.map((issue) => <li key={issue}>{issue}</li>)}
        </ul>
      )}
    </>
  );
}

function PreviewDetail({ preview }: { preview: NonNullable<SourcesPage['preview']> }) {
  return (
    <>
      {preview.listUnreadable && <p>The file list could not be read. Preview again.</p>}
      {!preview.listUnreadable && preview.files.length > 0 && (
        <table>
          <caption>Files that would change</caption>
          <thead>
            <tr>
              <th scope="col">File</th>
              <th scope="col">What would change</th>
            </tr>
          </thead>
          <tbody>
            {preview.files.map((f) => (
              <tr key={`${f.change}-${f.label}`}>
                <td>{f.label}</td>
                <td>{CHANGE[f.change] ?? f.change}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {preview.questions.length > 0 && (
        <>
          <h4>Questions for CDW</h4>
          <ul>
            {preview.questions.map((q) => (
              <li key={q.question}>
                {q.description ? `${q.description}. ` : ''}
                {q.question} {q.findings === 0 ? 'No finding is linked to this yet.' : q.findings === 1 ? '1 finding.' : `${q.findings} findings.`}
              </li>
            ))}
          </ul>
        </>
      )}
      {preview.firms.map((f) => (
        <section key={f.firmCode} aria-labelledby={`sources-${f.firmCode}`}>
          <h4 id={`sources-${f.firmCode}`}>{f.firmCode}</h4>
          {f.detailStale || !f.detail ? (
            <p>Run Preview again to read this firm&apos;s wording.</p>
          ) : (
            <>
              {f.detail.unreviewed.length > 0 && (
                <>
                  <h5>New descriptions to review</h5>
                  <ul>
                    {f.detail.unreviewed.map((u) => (
                      <li key={u.description}>{u.description} ({u.riskCodes.join(', ') || 'no risk code'})</li>
                    ))}
                  </ul>
                </>
              )}
              {f.detail.familyLinked.filter((u) => !f.detail!.unreviewed.some((x) => x.description === u.title)).length > 0 && (
                <>
                  <h5>Linked by family, to review</h5>
                  <ul>
                    {f.detail.familyLinked.filter((u) => !f.detail!.unreviewed.some((x) => x.description === u.title)).map((u) => (
                      <li key={`fam-${u.riskCode}`}>{u.riskCode}: {u.title}</li>
                    ))}
                  </ul>
                </>
              )}
              {f.detail.notLinked.length > 0 && (
                <>
                  <h5>Not linked to a control yet</h5>
                  <ul>
                    {f.detail.notLinked.map((u) => (
                      <li key={`none-${u.riskCode}`}>{u.riskCode}: {u.title}</li>
                    ))}
                  </ul>
                </>
              )}
              {f.detail.unreviewed.length + f.detail.familyLinked.length + f.detail.notLinked.length === 0 && <p>No descriptions to review.</p>}
            </>
          )}
        </section>
      ))}
    </>
  );
}
